import {
  ArrowUpRightIcon,
  DownloadSimpleIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { Button, Popover, Tooltip, ActionsButton } from '@skupreme/shared/ui';
import { tooltipTexts } from '@utility/constants/common';
import { ReadOnlyTip, useOrdersV2 } from '../../hooks/orders-v2';

export type OrdersActionsProps = {
  onNewOrder: () => void;
  onExport: () => void;
  /** Given only on the queues an order can be imported into. */
  onImport?: () => void;
  isDisabled?: boolean;
  isHidden?: boolean;
};

type ActionProps = {
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
  isDisabled?: boolean;
};

function Action({ icon, label, onPress, isDisabled }: ActionProps) {
  return (
    <Button
      variant="menu"
      size="md"
      className="sk:gap-2"
      isDisabled={isDisabled}
      onPress={onPress}
    >
      {icon}
      {label}
    </Button>
  );
}

export function OrdersActions({
  onNewOrder,
  onExport,
  onImport,
  isDisabled,
  isHidden,
}: OrdersActionsProps) {
  /* Not on V2: every action here (new order popup, import, export from the legacy order cache) is legacy. */
  const { isV2 } = useOrdersV2();
  if (isV2) return null;

  return (
    <Popover
      aria-label="Order actions"
      placement="bottom end"
      trigger={<ActionsButton />}
    >
      {({ close }) => (
        <div className="sk:flex sk:w-44 sk:flex-col sk:gap-0.5">
          <ReadOnlyTip triggerClassName="sk:w-full">
            <Action
              icon={<PlusIcon size={14} />}
              label="New order"
              onPress={() => {
                onNewOrder();
                close();
              }}
            />
          </ReadOnlyTip>

          {isHidden ? null : (
            <Tooltip
              content={tooltipTexts.missingPermission}
              placement="left"
              isDisabled={!isDisabled}
              triggerClassName="sk:w-full"
            >
              <span className="sk:flex sk:w-full sk:flex-col sk:gap-0.5">
                {onImport ? (
                  <ReadOnlyTip triggerClassName="sk:w-full">
                    <Action
                      icon={<ArrowUpRightIcon size={14} />}
                      label="Import"
                      isDisabled={isDisabled}
                      onPress={() => {
                        onImport();
                        close();
                      }}
                    />
                  </ReadOnlyTip>
                ) : null}
                <Action
                  icon={<DownloadSimpleIcon size={14} />}
                  label="Export"
                  isDisabled={isDisabled}
                  onPress={() => {
                    onExport();
                    close();
                  }}
                />
              </span>
            </Tooltip>
          )}
        </div>
      )}
    </Popover>
  );
}
